import { useRef } from "react";
import { IconFileImport, IconPlus, IconTrash } from "@tabler/icons-react";
import { Button, Card, Group, IconAction, Input, NumberInput, Stack, Text } from "@/components/ui";
import type { EntradaAlmacenInput } from "@/lib/types";
import { ConceptosEditor } from "./ConceptosEditor";

/** Campo de solo lectura (label + valor) para el modo read-only. */
function CampoRO({ label, valor, mono }: { label: string; valor: string; mono?: boolean }) {
  return (
    <Stack gap={0}>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text size="sm" className={mono ? "font-mono" : undefined}>{valor}</Text>
    </Stack>
  );
}

export function entradaVacia(claveOrdenCompra: string, moneda: string): EntradaAlmacenInput {
  return {
    folio: "",
    claveOrdenCompra,
    fecha: null,
    subTotal: 0,
    impuestos: 0,
    total: 0,
    moneda,
    anula: null,
    referencia: null,
    observaciones: null,
    conceptos: [],
  };
}

interface Props {
  value: EntradaAlmacenInput[];
  onChange: (v: EntradaAlmacenInput[]) => void;
  claveOrdenCompra: string;
  moneda: string;
  /** Si se provee, muestra "Importar entradas desde XML" · recibe los archivos elegidos (uno o varios). */
  onImportarXml?: (files: File[]) => void;
  /** Import en curso (deshabilita el botón). */
  importando?: boolean;
  /**
   * Solo lectura · las entradas provienen del XML y no se editan en el portal.
   * Se conservan importar (registra el XML tal cual) y eliminar (quita una entrada
   * de la lista), pero sus datos y conceptos no son editables.
   */
  readOnly?: boolean;
}

/** Editor controlado de entradas de almacén de una OC (cada una con sus conceptos). */
export function EntradasEditor({
  value,
  onChange,
  claveOrdenCompra,
  moneda,
  onImportarXml,
  importando = false,
  readOnly = false,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const upd = (i: number, patch: Partial<EntradaAlmacenInput>) =>
    onChange(value.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));
  const del = (i: number) => onChange(value.filter((_, idx) => idx !== i));
  const add = () => onChange([...value, entradaVacia(claveOrdenCompra, moneda)]);

  const onFilesElegidos = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (files.length > 0) onImportarXml?.(files);
  };

  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Text size="sm" fw={600}>Entradas de almacén ({value.length})</Text>
        <Group gap="xs">
          {onImportarXml && (
            <>
              <input
                ref={inputRef}
                type="file"
                accept="text/xml,application/xml,.xml"
                multiple
                onChange={onFilesElegidos}
                className="absolute h-px w-px opacity-0"
                style={{ pointerEvents: "none" }}
              />
              <Button
                size="xs"
                variant="light"
                leftSection={<IconFileImport size={14} />}
                loading={importando}
                onClick={() => inputRef.current?.click()}
              >
                Importar entradas desde XML
              </Button>
            </>
          )}
          {!readOnly && (
            <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={add}>
              Agregar entrada
            </Button>
          )}
        </Group>
      </Group>

      {value.length === 0 ? (
        <Text size="xs" c="dimmed">Sin entradas · opcional.</Text>
      ) : (
        value.map((e, i) => (
          <Card key={i} padding="sm">
            <Stack gap="sm">
              <Group justify="space-between">
                <Text size="sm" fw={600}>Entrada {e.folio || "(sin folio)"}</Text>
                <IconAction label="Eliminar entrada" size="sm" iconColor="peligro-600" onClick={() => del(i)}>
                  <IconTrash size={14} />
                </IconAction>
              </Group>
              {readOnly ? (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                  <CampoRO label="Folio" valor={e.folio} mono />
                  <CampoRO label="Referencia" valor={e.referencia ?? "—"} />
                  <CampoRO label="Anula (folio)" valor={e.anula ?? "—"} mono />
                  <CampoRO label="Moneda" valor={e.moneda} />
                  <CampoRO label="Subtotal" valor={String(e.subTotal)} />
                  <CampoRO label="Impuestos" valor={String(e.impuestos)} />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  <Input size="xs" label="Folio" value={e.folio} onChange={(ev) => upd(i, { folio: ev.currentTarget.value })} />
                  <Input size="xs" label="Referencia" value={e.referencia ?? ""} onChange={(ev) => upd(i, { referencia: ev.currentTarget.value || null })} />
                  <Input size="xs" label="Anula (folio)" value={e.anula ?? ""} onChange={(ev) => upd(i, { anula: ev.currentTarget.value || null })} />
                  <Input size="xs" label="Moneda" value={e.moneda} onChange={(ev) => upd(i, { moneda: ev.currentTarget.value })} />
                  <NumberInput size="xs" label="Subtotal" hideControls decimals={4} value={e.subTotal} onChange={(v) => upd(i, { subTotal: Number(v) || 0 })} />
                  <NumberInput size="xs" label="Impuestos" hideControls decimals={4} value={e.impuestos} onChange={(v) => upd(i, { impuestos: Number(v) || 0 })} />
                </div>
              )}
              <ConceptosEditor value={e.conceptos} onChange={(cs) => upd(i, { conceptos: cs })} simple readOnly={readOnly} />
            </Stack>
          </Card>
        ))
      )}
    </Stack>
  );
}
